// Reveals elements as they scroll into view, staggering siblings so a row of
// cards rises in sequence rather than all at once.
//
// The hidden starting state lives behind an `html.js` class (set by an inline
// script in the layout), so if this module fails to load the content is simply
// visible rather than stuck at opacity 0.

const DEFAULT_STAGGER_MS = 60;
const ROOT_MARGIN = '0px 0px -10% 0px';

// Dropping data-reveal once the entrance has played hands the element back to
// its own stylesheet. Cards carry a hover transform of their own, and the
// reveal rule (higher specificity) would otherwise keep overriding it.
function releaseRevealStyles(el) {
  el.removeAttribute('data-reveal');
  el.style.removeProperty('--reveal-delay');
}

function reveal(el) {
  el.classList.add('is-revealed');
  el.addEventListener('transitionend', () => releaseRevealStyles(el), { once: true });
  // Fallback for when no transition runs (or is interrupted) and transitionend
  // never fires.
  setTimeout(() => releaseRevealStyles(el), 1200);
}

function init() {
  const targets = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
  if (targets.length === 0) return;

  // No IntersectionObserver, or the visitor prefers reduced motion: show
  // everything immediately and skip the animation entirely.
  if (
    !('IntersectionObserver' in window) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    targets.forEach(releaseRevealStyles);
    return;
  }

  // Stagger by position among the siblings that share a parent, so each group
  // of cards counts from zero. A container can slow its own sequence down with
  // data-reveal-stagger.
  const indexInGroup = new Map();
  targets.forEach((el) => {
    const parent = el.parentElement;
    const next = (indexInGroup.get(parent) ?? -1) + 1;
    indexInGroup.set(parent, next);

    const step = Number(parent?.dataset.revealStagger) || DEFAULT_STAGGER_MS;
    el.style.setProperty('--reveal-delay', `${next * step}ms`);
  });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: ROOT_MARGIN }
  );

  targets.forEach((el) => observer.observe(el));
}

init();

// The theme editor re-renders sections without a page load.
document.addEventListener('shopify:section:load', init);

// Several components replace their own innerHTML from a fetch — product
// recommendations, the filtered collection grid, the cart drawer. Cards arriving
// that way were never handed to the observer, so they stayed at opacity 0 and
// looked like missing content. Watching the document catches them wherever they
// appear, rather than relying on every component to remember to announce itself.
if ('MutationObserver' in window) {
  let queued = false;
  const contentObserver = new MutationObserver((records) => {
    if (queued) return;
    const addedReveal = records.some((record) =>
      Array.from(record.addedNodes).some(
        (node) =>
          node.nodeType === 1 &&
          (node.matches?.('[data-reveal]') || node.querySelector?.('[data-reveal]'))
      )
    );
    if (!addedReveal) return;

    // Coalesce a burst of mutations into one pass on the next frame.
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      init();
    });
  });
  contentObserver.observe(document.body, { childList: true, subtree: true });
}
